import api from './api';

/**
 * Master Agent API - Two-Phase Execution Flow
 *
 * Phase 1: Generate queries and collect parameters from user
 * Phase 2: Execute queries with user-provided parameters
 */

const API_BASE = '/api/agents';

/**
 * Get agent orchestration plans for a database (conversation history).
 * Returns plans with steps, results, and step execution summary.
 * @param {number} databaseId - ID of the database
 */
export const getConversationHistory = (databaseId) =>
    api.get(`${API_BASE}/plans/${databaseId}`);

/**
 * Get all sub-agent definitions for a database.
 * @param {number} databaseId - ID of the database
 */
export const getSubAgents = (databaseId) =>
    api.get(`${API_BASE}/sub-agents/${databaseId}`);

/**
 * Get generated queries for a database.
 * @param {number} databaseId - ID of the database
 */
export const getGeneratedQueries = (databaseId) =>
    api.get(`${API_BASE}/queries/${databaseId}`);

/**
 * Get all insights for a database.
 * @param {number} databaseId - ID of the database
 * @param {string} flowType - Optional flow type filter: 'nlp' or 'sub_agent'
 * @param {number} subAgentId - Optional sub-agent ID filter (used with flow_type='sub_agent')
 */
export const getInsights = (databaseId, flowType = null, subAgentId = null) => {
    let url = `${API_BASE}/insights/${databaseId}`;
    const params = [];
    if (flowType) params.push(`flow_type=${flowType}`);
    if (subAgentId) params.push(`sub_agent_id=${subAgentId}`);
    if (params.length > 0) url += '?' + params.join('&');
    return api.get(url);
};

/**
 * Generate queries for insights.
 * @param {number[]} insightIds - Array of insight IDs
 */
export const generateQuery = (insightIds) =>
    api.post(`${API_BASE}/queries/generate`, { insight_ids: insightIds });

/**
 * Get generated queries for a database.
 * @param {number} databaseId - ID of the database
 * @param {number} insightId - Optional filter by insight ID
 * @param {string} status - Optional filter by execution status
 */
export const getQueries = (databaseId, insightId = null, status = null) => {
    let url = `${API_BASE}/queries/${databaseId}`;
    const params = [];
    if (insightId) params.push(`insight_id=${insightId}`);
    if (status) params.push(`status=${status}`);
    if (params.length > 0) url += '?' + params.join('&');
    return api.get(url);
};

/**
 * Execute a generated query and analyze results.
 * @param {number} queryId - ID of the query to execute
 * @param {number} databaseId - ID of the database
 * @param {Object} parameters - Optional parameter values for query execution
 */
export const executeQuery = (queryId, databaseId, parameters = null) =>
    api.post(`${API_BASE}/queries/execute`, {
        query_id: queryId,
        database_id: databaseId,
        parameters: parameters
    });

/**
 * Get detailed execution history for a specific query.
 * @param {number} queryId - ID of the generated query
 */
export const getQueryExecutionDetails = (queryId) =>
    api.get(`${API_BASE}/executions/query/${queryId}`);

/**
 * Get execution history for a specific generated query.
 * @param {number} queryId - ID of the generated query
 * @param {number} limit - Optional limit on results (default 50)
 */
export const getQueryExecutionHistory = (queryId, limit = 50) =>
    api.get(`${API_BASE}/executions/query/${queryId}/history?limit=${limit}`);

/**
 * Get insights for a sub-agent (fetches existing ones from database).
 * @param {number} agentId - ID of the sub-agent
 */
export const getSubAgentInsights = (agentId) =>
    api.get(`${API_BASE}/sub-agent/${agentId}/insights`);

/**
 * Generate insights for a sub-agent using the insight generation prompt.
 * This endpoint ONLY calls the insight generation prompt with sub-agent specific context.
 * @param {number} agentId - ID of the sub-agent
 */
export const generateSubAgentInsights = (agentId) =>
    api.post(`${API_BASE}/sub-agent/${agentId}/insights/generate`);

/**
 * Generate queries from insights.
 * @param {number[]} insightIds - Array of insight IDs
 */
export const generateQueriesFromInsights = (insightIds) =>
    api.post(`${API_BASE}/queries/generate-from-insights`, {
        insight_ids: insightIds
    });

/**
 * Step 4: Execute a query with user-provided parameters.
 * @param {number} queryId - ID of the query to execute
 * @param {number} databaseId - ID of the database
 * @param {Object} parameters - Parameter values provided by the user
 */
export const executeQueryWithParameters = (queryId, databaseId, parameters) =>
    api.post(`${API_BASE}/queries/execute-with-parameters`, {
        query_id: queryId,
        database_id: databaseId,
        parameters: parameters
    });

/**
 * Get available tables for a database from schema analysis.
 * @param {number} databaseId - ID of the database
 */
export const getAvailableTables = async (databaseId) => {
    try {
        console.log('[getAvailableTables] Fetching for database ID:', databaseId);
        const response = await api.get(`/api/schema/${databaseId}`);
        console.log('[getAvailableTables] API Response:', response.data);
        
        if (response.data?.status === 'success') {
            // Navigate the nested structure: response.data.data.analysis.schema_info
            const analysisData = response.data.data?.analysis || response.data.data;
            console.log('[getAvailableTables] Analysis Data:', analysisData);
            
            const schemaInfo = analysisData.schema_info || {};
            console.log('[getAvailableTables] Schema Info:', schemaInfo);
            
            // Try multiple possible fields: tables, table_names
            const tables = schemaInfo.tables || schemaInfo.table_names || [];
            console.log('[getAvailableTables] Tables:', tables);
            
            // Return array of table names or table objects
            if (tables && Array.isArray(tables)) {
                return tables;
            }
        }
    } catch (error) {
        console.error('[getAvailableTables] Error:', error);
    }
    console.log('[getAvailableTables] Returning empty array');
    return [];
};

/**
 * Sub-Agent Editor API Functions
 */

/**
 * Extract agent configuration from markdown document.
 * @param {string} markdown - The markdown document to parse
 * @param {number} databaseConnectionId - Database connection ID to provide context
 */
// NOTE: markdown extraction endpoint was removed. Sub-agents are edited directly via `.claude/agents/*.md`.

/**
 * Get all sub-agent definitions for a database.
 * @param {number} databaseId - Database connection ID
 * @param {number} schemaAnalysisId - Optional schema analysis ID to filter by
 */
export const getSubAgentDefinitions = (databaseId, schemaAnalysisId = null) => {
    let url = `${API_BASE}/definitions/${databaseId}`;
    if (schemaAnalysisId) url += `?schema_analysis_id=${schemaAnalysisId}`;
    return api.get(url);
};

/**
 * Get a single sub-agent definition by ID.
 * @param {number} agentId - Sub-agent definition ID
 */
export const getSubAgentDefinition = (agentId) =>
    api.get(`${API_BASE}/definition/${agentId}`);

/**
 * Create a new sub-agent definition.
 * @param {Object} agentData - Agent configuration data
 */
export const createSubAgentDefinition = (agentData) =>
    api.post(`${API_BASE}/definition/create`, agentData);

/**
 * Update an existing sub-agent definition.
 * @param {number} agentId - Sub-agent definition ID
 * @param {Object} agentData - Agent configuration data to update
 */
export const updateSubAgentDefinition = (agentId, agentData) =>
    api.put(`${API_BASE}/definition/${agentId}/update`, agentData);

/**
 * Delete a sub-agent definition.
 * @param {number} agentId - Sub-agent definition ID
 */
export const deleteSubAgentDefinition = (agentId) =>
    api.delete(`${API_BASE}/definition/${agentId}/delete`);

/**
 * ============================================================================
 * NEW AGENT CREATION FLOW API FUNCTIONS
 * ============================================================================
 * These functions support the UI flow shown in mark.md images:
 * 1. Create Agent (Image 1) → 2. Agent List (Image 2) → 3. Agent Settings (Images 3-5)
 */

/**
 * Get insights for a specific agent.
 * @param {number} agentId - Agent ID to get insights for
 * @param {number} databaseId - Database ID the agent belongs to
 */
export const getInsightsForAgent = (agentId, databaseId) =>
    api.get(`${API_BASE}/insights/${databaseId}`, {
        params: {
            flow_type: 'sub_agent',
            sub_agent_id: agentId
        }
    });

/**
 * Get insights count for a sub-agent.
 * @param {number} databaseId - Database ID
 * @param {number} subAgentId - Sub-agent ID filter
 */
export const getSubAgentInsightsCount = (databaseId, subAgentId) =>
    api.get(`${API_BASE}/insights/${databaseId}`, {
        params: {
            flow_type: 'sub_agent',
            sub_agent_id: subAgentId
        }
    });

/**
 * Get all master agents with their sub-agents.
 */
export const getMasterAgents = () =>
    api.get(`${API_BASE}/master-agents`);

/**
 * Create a new master agent.
 * @param {Object} agentData - Agent configuration data { name, database_connection_id, description }
 */
export const createMasterAgent = (agentData) =>
    api.post(`${API_BASE}/master-agent/create`, agentData);

/**
 * Get agent settings for the agent configuration page.
 * @param {number} agentId - Agent ID
 */
// NOTE: agent settings endpoints were removed (markdown-first agents).

/**
 * Ask a sub-agent a question (chat) - Check if existing agents are sufficient.
 * This endpoint ONLY checks sufficiency - it does NOT create new agents.
 * @param {number} databaseId - Database ID
 * @param {string} question - The question to ask
 */
export const checkAgentSufficiency = (databaseId, question) =>
    api.post(`${API_BASE}/ask/check-agents?database_id=${databaseId}`, {
        question
    });

/**
 * Ask a sub-agent a question (chat) - Create required agents.
 * This endpoint creates the sub-agents needed to answer the user's question.
 * @param {number} databaseId - Database ID
 * @param {string} question - The question to ask
 */
export const createRequiredAgents = (databaseId, question) =>
    api.post(`${API_BASE}/ask/create-required-agents?database_id=${databaseId}`, {
        question
    });

/**
 * Ask a sub-agent a question (chat) - Phase 1: Generate queries and get parameters.
 * @param {number} databaseId - Database ID
 * @param {string} question - The question to ask
 */
export const askSubAgentPhase1 = (databaseId, question) =>
    api.post(`${API_BASE}/ask/phase1?database_id=${databaseId}`, {
        question
    });

/**
 * Ask a sub-agent a question (chat) - Phase 2: Execute queries with user parameters.
 * @param {number} databaseId - Database ID
 * @param {number} nlpQaId - NLP Q&A ID from Phase 1 response
 * @param {Object} parametersByQuery - Dict of query_id -> {param_name: value}
 */
export const askSubAgentPhase2ExecuteAll = (databaseId, nlpQaId, parametersByQuery) =>
    api.post(`${API_BASE}/ask/phase2/execute-all?database_id=${databaseId}&nlp_qa_id=${nlpQaId}`, {
        parameters_by_query: parametersByQuery
    });

/**
 * Delete a specific query execution.
 * @param {number} executionId - ID of the execution to delete
 */
export const deleteQueryExecution = (executionId) =>
    api.delete(`${API_BASE}/executions/${executionId}`);

/**
 * Delete a specific conversation/plan.
 * @param {number} planId - ID of the plan/conversation to delete
 */
export const deleteConversation = (planId) =>
    api.delete(`${API_BASE}/plans/${planId}`);

/**
 * Delete a specific generated query.
 * @param {number} queryId - ID of the query to delete
 */
export const deleteGeneratedQuery = (queryId) =>
    api.delete(`${API_BASE}/queries/${queryId}`);

/**
 * Generate a query for an existing insight using user's natural language question.
 * @param {number} insightId - Existing insight ID
 * @param {string} userQuestion - User's natural language question
 */
export const generateQueryForInsight = (insightId, userQuestion) =>
    api.post(`${API_BASE}/queries/generate-for-insight`, {
        insight_id: insightId,
        user_question: userQuestion
    });

/**
 * Delete an insight and its associated generated queries.
 * @param {number} insightId - ID of the insight to delete
 */
export const deleteInsight = (insightId) =>
    api.delete(`${API_BASE}/insight/${insightId}/delete`);

/**
 * Generate a single insight for a sub-agent from user's natural language input.
 * @param {number} agentId - ID of the sub-agent
 * @param {string} userInput - User's natural language description of the insight they want
 */
export const generateInsightFromNLP = (agentId, userInput) =>
    api.post(`${API_BASE}/sub-agent/${agentId}/insights/generate-nlp`, {
        agent_id: agentId,
        user_input: userInput
    });

/**
 * Execute all queries for selected insights with consolidated parameters and get comprehensive analysis.
 * @param {number} agentId - ID of the sub-agent
 * @param {number} databaseId - ID of the database
 * @param {number[]} insightIds - Array of insight IDs to execute queries for
 * @param {Object} parametersByQuery - Dict of query_id -> {param_name: value}
 */
export const executeInsightsQueries = (agentId, databaseId, insightIds, parametersByQuery) =>
    api.post(`${API_BASE}/insights/execute?agent_id=${agentId}&database_id=${databaseId}`, {
        insight_ids: insightIds,
        parameters_by_query: parametersByQuery
    });

/**
 * Get a specific comprehensive analysis by ID.
 * @param {number} analysisId - ID of the analysis (agent_responses record)
 */
export const getInsightsAnalysis = (analysisId) =>
    api.get(`${API_BASE}/insights/analysis/${analysisId}`);

/**
 * Get all comprehensive analyses for a specific sub-agent.
 * @param {number} agentId - ID of the sub-agent
 * @param {number} databaseId - ID of the database
 * @param {number} limit - Maximum number of results (default 50)
 */
export const getSubAgentAnalyses = (agentId, databaseId, limit = 50) =>
    api.get(`${API_BASE}/sub-agent/${agentId}/analyses`, {
        params: { database_id: databaseId, limit }
    });


